import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaSave, FaSignOutAlt, FaYoutube, FaBlog, FaProjectDiagram, FaUser, FaCog, FaArrowLeft, FaRobot, FaSitemap, FaGraduationCap, FaBriefcase, FaCode, FaUserAlt, FaShareAlt, FaPlus, FaTrash, FaQuoteRight, FaLightbulb } from 'react-icons/fa';
import { API_BASE_URL } from '../config/apiConfig';

// Import tabs component
import SectionTabs from '../components/admin/SectionTabs';

// Import all admin components
import AboutEditor from '../components/admin/AboutEditor';
import SkillsEditor from '../components/admin/SkillsEditor';
import ProjectsEditor from '../components/admin/ProjectsEditor';
import ExperienceEditor from '../components/admin/ExperienceEditor';
import NewSection from '../components/admin/NewSection';
import ImageUploader from '../components/admin/ImageUploader';
import MessagesViewer from '../components/admin/MessagesViewer';
import FooterSettings from '../components/admin/FooterSettings';
import ChatbotSettings from '../components/admin/ChatbotSettings';
import { useSocialLinks } from '../context/SocialLinksContext';
import { usePortfolio } from '../context/PortfolioContext';
import TestimonialsEditor from '../components/admin/TestimonialsEditor';
import EducationEditor from '../components/admin/EducationEditor';
import InnovationsEditor from '../components/admin/InnovationsEditor';
import EditHero from '../components/admin/EditHero';
import SocialLinksEditor from '../components/admin/SocialLinksEditor';
import YouTubeEditor from '../components/admin/YouTubeEditor';
import PatentsEditor from '../components/admin/PatentsEditor';
import BlogEditor from '../components/admin/BlogEditor';
import SectionEditor from '../components/admin/SectionEditor';
import EmailSettings from '../components/admin/EmailSettings';

const AdminDashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/verify`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        if (!data.success) {
          navigate('/');
        }
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [settings, setSettings] = useState({
    enabled: true,
    welcomeMessage: "Hello! How can I help you today?",
    apiKey: "",
    aiEnabled: true,
    aiModel: "claude-3-sonnet",
    temperature: 0.7,
    maxTokens: 1000,
    responses: [
    {
      id: 1,
        question: "What services do you offer?",
        answer: "I offer AI development, web development, and mechanical engineering services."
    },
    {
      id: 2,
        question: "How can I contact you?",
        answer: "You can reach me through the contact form on my website or via email at dhiraj@example.com."
      },
      {
        id: 3,
        question: "What is your experience?",
        answer: "I have extensive experience in AI engineering, web development, and mechanical innovations. I've worked on various projects including AI solutions, web applications, and mechanical designs."
      },
      {
        id: 4,
        question: "Do you offer freelance services?",
        answer: "Yes, I offer freelance services in AI development, web development, and mechanical engineering. Feel free to contact me to discuss your project requirements."
      },
      {
        id: 5,
        question: "What technologies do you work with?",
        answer: "I work with a wide range of technologies including AI/ML frameworks, web development stacks (React, Node.js), and mechanical engineering tools. I'm constantly learning and adapting to new technologies."
      }
    ]
  });
  
  const { portfolioData, updatePortfolioData, groupedSkills, loading: portfolioLoading } = usePortfolio();
  
  // Always initialize state from portfolioData, and sync when it changes
  const [profileData, setProfileData] = useState(portfolioData?.profile || {});
  const [aboutData, setAboutData] = useState(portfolioData?.about || {});
  // Remove: const [skillsData, setSkillsData] = useState(portfolioData?.skills || []);
  const [experienceData, setExperienceData] = useState(portfolioData?.experience || []);
  const [educationData, setEducationData] = useState(portfolioData?.education || []);
  const [blogData, setBlogData] = useState(portfolioData?.blog || []);
  const [youtubeData, setYoutubeData] = useState(portfolioData?.youtube || []);
  const [projectsData, setProjectsData] = useState(portfolioData?.projects || []);
  const [innovationsData, setInnovationsData] = useState(portfolioData?.innovations || []);
  const [testimonialsData, setTestimonialsData] = useState(portfolioData?.testimonials || []);

  const { socialLinks, updateSocialLinks } = useSocialLinks();
  const [socialLinksData, setSocialLinksData] = useState(socialLinks);

  const [content, setContent] = useState({});

  useEffect(() => {
    if (portfolioData && !hasChanges) {
      const profile = portfolioData.profile || {};
      setProfileData({
        ...profile,
        name: profile.name || 'Your Name',
        roles: Array.isArray(profile.roles) && profile.roles.length > 0 ? profile.roles : ["AI Engineer", "Freelancer", "YouTuber", "Innovator"],
        tagline: typeof profile.tagline === 'string' ? profile.tagline : "Building AI and web solutions."
      });
      setAboutData(portfolioData.about || {});
      // Remove: setSkillsData(portfolioData.skills || []);
      setExperienceData(portfolioData.experience || []);
      setEducationData(portfolioData.education || []);
      setBlogData(portfolioData.blog || []);
      setYoutubeData(portfolioData.youtube || []);
      setProjectsData(portfolioData.projects || []);
      setInnovationsData(portfolioData.innovations || []);
      setTestimonialsData(portfolioData.testimonials || []);
    }
  }, [portfolioData, hasChanges]);

  useEffect(() => {
    // Set loading to false immediately since we don't need authentication
    setLoading(false);
  }, []);
  
  const handleBackToHome = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to go back?')) {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  };
  
  const handleSave = async (section) => {
    try {
      setIsSaving(true);
      
      // Save to localStorage
      localStorage.setItem(`${section}Content`, content[section]);
      
      // If there are changes to save to the backend
      if (hasChanges) {
        // Here you would typically make an API call to save the changes
        // For now, we'll just simulate a successful save
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Update the change flag
        setHasChanges(false);
      }
      
      // Show success message
      setSaveStatus({
        type: 'success',
        message: 'Changes saved successfully!'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving changes:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to save changes. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = (section, newContent) => {
    setContent(prev => ({
      ...prev,
      [section]: newContent
    }));
    setHasChanges(true);
  };

  // Load content from localStorage on component mount
  useEffect(() => {
    const loadContent = () => {
      const sections = ['about', 'skills', 'projects', 'experience'];
      const loadedContent = {};
      
      sections.forEach(section => {
        const savedContent = localStorage.getItem(`${section}Content`);
        if (savedContent) {
          loadedContent[section] = savedContent;
        }
      });
      
      setContent(prev => ({
        ...prev,
        ...loadedContent
      }));
    };
    
    loadContent();
  }, []);
  
  const saveChanges = async () => {
    setLoading(true);
    try {
      await updatePortfolioData('profile', profileData);
      // Optionally update other sections here if needed
      setHasChanges(false);
      // After context refresh, update local state from context
      setTimeout(() => {
        if (portfolioData && portfolioData.profile) {
          setProfileData({
            ...portfolioData.profile,
            roles: Array.isArray(portfolioData.profile.roles) && portfolioData.profile.roles.length > 0 ? portfolioData.profile.roles : ["AI Engineer", "Freelancer", "YouTuber", "Innovator"],
            tagline: typeof portfolioData.profile.tagline === 'string' ? portfolioData.profile.tagline : "Building AI and web solutions."
          });
        }
      }, 500); // Give context a moment to refresh
      alert('Changes saved successfully!');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Profile data handlers
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);
  };
  
  // Blog data handlers
  const handleBlogChange = (index, field, value) => {
    const updatedBlogs = [...blogData];
    updatedBlogs[index][field] = value;
    setBlogData(updatedBlogs);
    setHasChanges(true);
  };
  
  const addBlog = () => {
    const newBlog = {
      id: Date.now(),
      slug: "new-blog-post-" + Date.now(),
      title: "New Blog Post",
      excerpt: "Write your excerpt here...",
      coverImage: "/blog/default.jpg",
      publishedAt: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      tags: ["New"],
      readTime: "5 min read"
    };
    
    setBlogData(prev => [...prev, newBlog]);
    setHasChanges(true);
  };
  
  const removeBlog = (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      setBlogData(prev => prev.filter(blog => blog.id !== id));
      setHasChanges(true);
    }
  };
  
  // YouTube data handlers
  const handleYouTubeChange = (index, field, value) => {
    const updatedChannels = [...youtubeData];
    updatedChannels[index][field] = value;
    setYoutubeData(updatedChannels);
    setHasChanges(true);
  };
  
  const addYouTubeChannel = () => {
    const newChannel = {
      id: Date.now(),
      name: "New Channel",
      handle: "@new-channel",
      url: "https://youtube.com/@new-channel",
      description: "Add a description for your channel",
      subscribers: "0",
      apiKey: ""
    };
    
    setYoutubeData(prev => [...prev, newChannel]);
    setHasChanges(true);
  };
  
  const removeYouTubeChannel = (id) => {
    if (window.confirm('Are you sure you want to delete this YouTube channel?')) {
      setYoutubeData(prev => prev.filter(channel => channel.id !== id));
      setHasChanges(true);
    }
  };
  
  // Projects data handlers
  const handleProjectChange = (index, field, value) => {
    const updatedProjects = [...projectsData];
    updatedProjects[index][field] = value;
    setProjectsData(updatedProjects);
    setHasChanges(true);
  };
  
  const addProject = () => {
    const newProject = {
      id: Date.now(),
      title: "New Project",
      description: "Describe your project here...",
      image: "/projects/default.jpg",
      tags: ["New"],
    };
    
    setProjectsData(prev => [...prev, newProject]);
    setHasChanges(true);
  };
  
  const removeProject = (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      setProjectsData(prev => prev.filter(project => project.id !== id));
      setHasChanges(true);
    }
  };
  
  // Save settings handlers
  const handleChatbotSettingsSave = (data) => {
    console.log('Saving chatbot settings:', data);
    setHasChanges(true);
  };
  
  const handleFooterSettingsSave = (data) => {
    console.log('Saving footer settings:', data);
    setHasChanges(true);
  };
  
  // About data handlers
  const handleAboutChange = (field, value) => {
    setAboutData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Skills data handlers
  // Remove all local skills state and pass nothing for skills to SkillsEditor
  // const handleSkillChange = (category, index, field, value) => {
  //   const updatedSkills = [...skillsData[category]];
  //   updatedSkills[index] = {
  //     ...updatedSkills[index],
  //     [field]: value
  //   };
    
  //   // Update local state
  //   setSkillsData(prev => ({
  //     ...prev,
  //     [category]: updatedSkills
  //   }));
    
  //   // Update portfolio context
  //   const updatedPortfolioData = {
  //     ...portfolioData,
  //     skills: {
  //       ...portfolioData.skills,
  //       [category]: updatedSkills
  //     }
  //   };
  //   updatePortfolioData('skills', updatedPortfolioData.skills);
    
  //   setHasChanges(true);
  // };

  // const addSkill = (category, skillType) => {
  //   const newSkill = {
  //     name: skillType === 'frontend' ? 'New Frontend Skill' :
  //           skillType === 'backend' ? 'New Backend Skill' :
  //           skillType === 'mechanical' ? 'New Mechanical Skill' :
  //           'New Soft Skill',
  //     level: 50
  //   };
    
  //   // Create a new array with the new skill
  //   const updatedSkills = [...skillsData[category], newSkill];
    
  //   // Update local state
  //   setSkillsData(prev => ({
  //     ...prev,
  //     [category]: updatedSkills
  //   }));
    
  //   // Update portfolio context
  //   const updatedPortfolioData = {
  //     ...portfolioData,
  //     skills: {
  //       ...portfolioData.skills,
  //       [category]: updatedSkills
  //     }
  //   };
  //   updatePortfolioData('skills', updatedPortfolioData.skills);
    
  //   setHasChanges(true);
  // };

  // const removeSkill = (category, index) => {
  //   if (window.confirm('Are you sure you want to remove this skill?')) {
  //     const updatedSkills = [...skillsData[category]];
  //     updatedSkills.splice(index, 1);
      
  //     // Update local state
  //     setSkillsData(prev => ({
  //       ...prev,
  //       [category]: updatedSkills
  //     }));
      
  //     // Update portfolio context
  //     const updatedPortfolioData = {
  //       ...portfolioData,
  //       skills: {
  //         ...portfolioData.skills,
  //         [category]: updatedSkills
  //       }
  //     };
  //     updatePortfolioData('skills', updatedPortfolioData.skills);
      
  //     setHasChanges(true);
  //   }
  // };

  // Experience data handlers
  const handleExperienceChange = (index, field, value) => {
    const updatedExperience = [...experienceData];
    updatedExperience[index][field] = value;
    setExperienceData(updatedExperience);
    setHasChanges(true);
  };

  const handleAchievementChange = (expIndex, achievementIndex, value) => {
    const updatedExperience = [...experienceData];
    updatedExperience[expIndex].achievements[achievementIndex] = value;
    setExperienceData(updatedExperience);
    setHasChanges(true);
  };

  const addExperience = () => {
    const newExperience = {
      id: Date.now(),
      role: "New Position",
      company: "Company Name",
      period: "Period",
      description: "Description",
      achievements: ["Achievement 1"],
      technologies: ["Technology 1"]
    };
    setExperienceData(prev => [...prev, newExperience]);
    setHasChanges(true);
  };

  const removeExperience = (id) => {
    if (window.confirm('Are you sure you want to remove this experience?')) {
      setExperienceData(prev => prev.filter(exp => exp.id !== id));
      setHasChanges(true);
    }
  };

  // Education data handlers
  const handleEducationChange = (index, field, value) => {
    const updatedEducation = [...educationData];
    updatedEducation[index][field] = value;
    setEducationData(updatedEducation);
    setHasChanges(true);
  };

  const handleEducationAchievementChange = (eduIndex, achievementIndex, value) => {
    const updatedEducation = [...educationData];
    updatedEducation[eduIndex].achievements[achievementIndex] = value;
    setEducationData(updatedEducation);
    setHasChanges(true);
  };

  const addEducation = () => {
    const newEducation = {
      id: Date.now(),
      degree: "New Degree",
      institution: "Institution Name",
      period: "Period",
      description: "Description",
      achievements: ["Achievement 1"]
    };
    setEducationData(prev => [...prev, newEducation]);
    setHasChanges(true);
  };

  const removeEducation = (id) => {
    if (window.confirm('Are you sure you want to remove this education entry?')) {
      setEducationData(prev => prev.filter(edu => edu.id !== id));
      setHasChanges(true);
    }
  };
  
  // Handler for changing a link
  const handleSocialLinkChange = (platform, idx, value) => {
    setSocialLinksData(prev => {
      const updated = { ...prev };
      updated[platform][idx] = value;
      return updated;
    });
    setHasChanges(true);
  };
  
  // Handler for adding a new link
  const addSocialLink = (platform) => {
    setSocialLinksData(prev => {
      const updated = { ...prev };
      updated[platform] = [...updated[platform], ""];
      return updated;
    });
    setHasChanges(true);
  };
  
  // Handler for removing a link
  const removeSocialLink = (platform, idx) => {
    setSocialLinksData(prev => {
      const updated = { ...prev };
      updated[platform] = updated[platform].filter((_, i) => i !== idx);
      return updated;
    });
    setHasChanges(true);
  };
  
  const renderSaveButton = () => (
    <button
      onClick={saveChanges}
      disabled={!hasChanges || loading}
      className={`btn btn-primary flex items-center gap-2 ${
        !hasChanges || loading ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <FaSave />
      {loading ? 'Saving...' : 'Save Changes'}
    </button>
  );

  const renderAboutSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-white mb-2">Main Text</label>
        <textarea
          name="mainText"
          value={aboutData.mainText}
          onChange={(e) => handleAboutChange('mainText', e.target.value)}
          className="w-full bg-gray-700 text-white rounded-md px-4 py-2"
          rows={4}
        />
      </div>
      
      <div>
        <label className="block text-white mb-2">Expanded Text</label>
        <textarea
          name="expandedText"
          value={aboutData.expandedText}
          onChange={(e) => handleAboutChange('expandedText', e.target.value)}
          className="w-full bg-gray-700 text-white rounded-md px-4 py-2"
          rows={4}
        />
      </div>

      <div>
        <label className="block text-white mb-2">Roles</label>
        {aboutData.roles.map((role, index) => (
          <div key={index} className="mb-4 p-4 bg-gray-800 rounded-lg">
            <input
              type="text"
              value={role.title}
              onChange={(e) => handleRoleChange(index, 'title', e.target.value)}
              className="w-full bg-gray-700 text-white rounded-md px-4 py-2 mb-2"
              placeholder="Role Title"
            />
            <textarea
              value={role.description}
              onChange={(e) => handleRoleChange(index, 'description', e.target.value)}
              className="w-full bg-gray-700 text-white rounded-md px-4 py-2"
              rows={2}
              placeholder="Role Description"
            />
          </div>
        ))}
        <button
          onClick={() => {
            const newRoles = [...aboutData.roles, { title: "New Role", description: "Description" }];
            setAboutData(prev => ({ ...prev, roles: newRoles }));
            setHasChanges(true);
          }}
          className="btn btn-primary mt-2"
        >
          Add Role
        </button>
      </div>
    </div>
  );

  const renderSkillsSection = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Frontend Development</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groupedSkills.technical.filter(skill => 
            ['React', 'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'Tailwind CSS', 'Redux', 'Next.js', 'Framer Motion', 'Material UI', 'SASS/SCSS'].includes(skill.name)
          ).map((skill, index) => (
            <motion.div
              key={index}
              className="bg-secondary/30 p-4 rounded-lg border border-primary/10 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ 
                backgroundColor: "rgba(34,197,94,0.4)",
                borderColor: "rgba(59,130,246,0.2)",
                scale: 1.01
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <input
                  type="text"
                  value={skill.name}
                  onChange={(e) => handleSkillChange('technical', index, 'name', e.target.value)}
                  className="w-full bg-primary/5 text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Skill Name"
                />
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => removeSkill('technical', index)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <FaTrash />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => addSkill('technical', 'frontend')}
          className="btn btn-primary mt-4 flex items-center gap-2"
        >
          <FaPlus /> Add Frontend Skill
        </motion.button>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Backend Development</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groupedSkills.technical.filter(skill => 
            ['Python', 'Node.js', 'Django', 'Flask', 'Express', 'MongoDB', 'PostgreSQL', 'GraphQL', 'REST API', 'Firebase', 'AWS', 'Docker'].includes(skill.name)
          ).map((skill, index) => (
            <motion.div
              key={index}
              className="bg-secondary/30 p-4 rounded-lg border border-primary/10 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ 
                backgroundColor: "rgba(34,197,94,0.4)",
                borderColor: "rgba(59,130,246,0.2)",
                scale: 1.01
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <input
                  type="text"
                  value={skill.name}
                  onChange={(e) => handleSkillChange('technical', index, 'name', e.target.value)}
                  className="w-full bg-primary/5 text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Skill Name"
                />
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => removeSkill('technical', index)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <FaTrash />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => addSkill('technical', 'backend')}
          className="btn btn-primary mt-4 flex items-center gap-2"
        >
          <FaPlus /> Add Backend Skill
        </motion.button>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Mechanical Engineering</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groupedSkills.technical.filter(skill => 
            ['CAD/CAM', '3D Printing', 'Robotics', 'IoT', 'Arduino', 'Raspberry Pi', 'PLC', 'Embedded Systems', 'Sensor Integration', 'Motor Control', 'Prototyping'].includes(skill.name)
          ).map((skill, index) => (
            <motion.div
              key={index}
              className="bg-secondary/30 p-4 rounded-lg border border-primary/10 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ 
                backgroundColor: "rgba(34,197,94,0.4)",
                borderColor: "rgba(59,130,246,0.2)",
                scale: 1.01
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <input
                  type="text"
                  value={skill.name}
                  onChange={(e) => handleSkillChange('technical', index, 'name', e.target.value)}
                  className="w-full bg-primary/5 text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Skill Name"
                />
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => removeSkill('technical', index)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <FaTrash />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => addSkill('technical', 'mechanical')}
          className="btn btn-primary mt-4 flex items-center gap-2"
        >
          <FaPlus /> Add Mechanical Skill
        </motion.button>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Soft Skills</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groupedSkills.soft.map((skill, index) => (
            <motion.div
              key={index}
              className="bg-secondary/30 p-4 rounded-lg border border-primary/10 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ 
                backgroundColor: "rgba(34,197,94,0.4)",
                borderColor: "rgba(59,130,246,0.2)",
                scale: 1.01
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <input
                  type="text"
                  value={skill.name}
                  onChange={(e) => handleSkillChange('soft', index, 'name', e.target.value)}
                  className="w-full bg-primary/5 text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Skill Name"
                />
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => removeSkill('soft', index)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <FaTrash />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => addSkill('soft')}
          className="btn btn-primary mt-4 flex items-center gap-2"
        >
          <FaPlus /> Add Soft Skill
        </motion.button>
      </div>
    </div>
  );

  const renderExperienceSection = () => (
    <div className="space-y-6">
      {experienceData.map((exp, index) => (
        <div key={exp.id} className="p-4 bg-gray-800 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={exp.role}
              onChange={(e) => handleExperienceChange(index, 'role', e.target.value)}
              className="bg-gray-700 text-white rounded-md px-4 py-2"
              placeholder="Position/Role"
            />
            <input
              type="text"
              value={exp.company}
              onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
              className="bg-gray-700 text-white rounded-md px-4 py-2"
              placeholder="Company Name"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={exp.period}
              onChange={(e) => handleExperienceChange(index, 'period', e.target.value)}
              className="bg-gray-700 text-white rounded-md px-4 py-2"
              placeholder="Period"
            />
            <input
              type="text"
              value={exp.location || ''}
              onChange={(e) => handleExperienceChange(index, 'location', e.target.value)}
              className="bg-gray-700 text-white rounded-md px-4 py-2"
              placeholder="Location"
            />
          </div>
          <textarea
            value={exp.description}
            onChange={(e) => handleExperienceChange(index, 'description', e.target.value)}
            className="w-full bg-gray-700 text-white rounded-md px-4 py-2 mb-4"
            rows={3}
            placeholder="Description"
          />
          <div className="space-y-2 mb-4">
            <label className="block text-white">Achievements</label>
            {(exp.achievements || []).map((achievement, aIndex) => (
              <input
                key={aIndex}
                type="text"
                value={achievement}
                onChange={(e) => handleAchievementChange(index, aIndex, e.target.value)}
                className="w-full bg-gray-700 text-white rounded-md px-4 py-2"
                placeholder={`Achievement ${aIndex + 1}`}
              />
            ))}
            <button
              onClick={() => {
                const updatedExp = [...experienceData];
                if (!updatedExp[index].achievements) updatedExp[index].achievements = [];
                updatedExp[index].achievements.push("");
                setExperienceData(updatedExp);
                setHasChanges(true);
              }}
              className="btn btn-primary mt-2"
            >
              Add Achievement
            </button>
          </div>
          <div className="space-y-2 mb-4">
            <label className="block text-white">Technologies</label>
            {(exp.technologies || []).map((tech, tIndex) => (
              <input
                key={tIndex}
                type="text"
                value={tech}
                onChange={(e) => {
                  const updatedExp = [...experienceData];
                  if (!updatedExp[index].technologies) updatedExp[index].technologies = [];
                  updatedExp[index].technologies[tIndex] = e.target.value;
                  setExperienceData(updatedExp);
                  setHasChanges(true);
                }}
                className="w-full bg-gray-700 text-white rounded-md px-4 py-2"
                placeholder={`Technology ${tIndex + 1}`}
              />
            ))}
            <button
              onClick={() => {
                const updatedExp = [...experienceData];
                if (!updatedExp[index].technologies) updatedExp[index].technologies = [];
                updatedExp[index].technologies.push("");
                setExperienceData(updatedExp);
                setHasChanges(true);
              }}
              className="btn btn-primary mt-2"
            >
              Add Technology
            </button>
          </div>
          <button
            onClick={() => removeExperience(exp.id)}
            className="btn btn-danger mt-4"
          >
            Remove Experience
          </button>
        </div>
      ))}
      <button
        onClick={addExperience}
        className="btn btn-primary"
      >
        Add Experience
      </button>
    </div>
  );

  const renderEducationSection = () => (
    <div className="space-y-6">
      {educationData.map((edu, index) => (
        <div key={edu.id} className="p-4 bg-gray-800 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={edu.degree}
              onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
              className="bg-gray-700 text-white rounded-md px-4 py-2"
              placeholder="Degree"
            />
            <input
              type="text"
              value={edu.institution}
              onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
              className="bg-gray-700 text-white rounded-md px-4 py-2"
              placeholder="Institution"
            />
          </div>
          <input
            type="text"
            value={edu.period}
            onChange={(e) => handleEducationChange(index, 'period', e.target.value)}
            className="w-full bg-gray-700 text-white rounded-md px-4 py-2 mb-4"
            placeholder="Period"
          />
          <textarea
            value={edu.description}
            onChange={(e) => handleEducationChange(index, 'description', e.target.value)}
            className="w-full bg-gray-700 text-white rounded-md px-4 py-2 mb-4"
            rows={3}
            placeholder="Description"
          />
          <div className="space-y-2">
            <label className="block text-white">Achievements</label>
            {edu.achievements.map((achievement, aIndex) => (
              <input
                key={aIndex}
                type="text"
                value={achievement}
                onChange={(e) => handleEducationAchievementChange(index, aIndex, e.target.value)}
                className="w-full bg-gray-700 text-white rounded-md px-4 py-2"
                placeholder={`Achievement ${aIndex + 1}`}
              />
            ))}
            <button
              onClick={() => {
                const updatedEdu = [...educationData];
                updatedEdu[index].achievements.push("");
                setEducationData(updatedEdu);
                setHasChanges(true);
              }}
              className="btn btn-primary mt-2"
            >
              Add Achievement
            </button>
          </div>
          <button
            onClick={() => removeEducation(edu.id)}
            className="btn btn-danger mt-4"
          >
            Remove Education
          </button>
        </div>
      ))}
      <button
        onClick={addEducation}
        className="btn btn-primary"
      >
        Add Education
      </button>
    </div>
  );

  // Update renderSocialLinksSection to support multiple links per platform and visibility toggle
  const renderSocialLinksSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.keys(socialLinksData).map(platform => (
          <div key={platform} className="bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-white font-medium capitalize">{platform}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-300">Visible</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={socialLinksData[platform].visible}
                    onChange={() => {
                      setSocialLinksData(prev => ({
                        ...prev,
                        [platform]: {
                          ...prev[platform],
                          visible: !prev[platform].visible
                        }
                      }));
                      setHasChanges(true);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
            {socialLinksData[platform].links.map((link, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input
                  type="url"
                  value={link}
                  onChange={e => {
                    const updatedLinks = [...socialLinksData[platform].links];
                    updatedLinks[idx] = e.target.value;
                    setSocialLinksData(prev => ({
                      ...prev,
                      [platform]: {
                        ...prev[platform],
                        links: updatedLinks
                      }
                    }));
                    setHasChanges(true);
                  }}
                  className="w-full bg-gray-600 text-white rounded-md px-4 py-2"
                  placeholder={`https://${platform}.com/username`}
                />
                <button 
                  type="button" 
                  onClick={() => {
                    const updatedLinks = socialLinksData[platform].links.filter((_, i) => i !== idx);
                    setSocialLinksData(prev => ({
                      ...prev,
                      [platform]: {
                        ...prev[platform],
                        links: updatedLinks
                      }
                    }));
                    setHasChanges(true);
                  }} 
                  className="text-red-400 hover:text-red-300"
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            <button 
              type="button" 
              onClick={() => {
                setSocialLinksData(prev => ({
                  ...prev,
                  [platform]: {
                    ...prev[platform],
                    links: [...prev[platform].links, ""]
                  }
                }));
                setHasChanges(true);
              }} 
              className="btn btn-primary btn-sm flex items-center gap-1 mt-2"
            >
              <FaPlus /> Add Link
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const handleInnovationChange = (index, field, value) => {
    const updatedInnovations = [...innovationsData];
    updatedInnovations[index][field] = value;
    setInnovationsData(updatedInnovations);
    setHasChanges(true);
  };

  const addInnovation = () => {
    const newInnovation = {
      id: Date.now(),
      title: "New Innovation",
      description: "Describe your innovation here...",
      image: "/innovations/default.jpg",
      technologies: ["New"],
      status: "Planning"
    };
    setInnovationsData(prev => [...prev, newInnovation]);
    setHasChanges(true);
  };

  const removeInnovation = (id) => {
    if (window.confirm('Are you sure you want to remove this innovation?')) {
      setInnovationsData(prev => prev.filter(innovation => innovation.id !== id));
      setHasChanges(true);
    }
  };

  const renderInnovationsSection = () => (
    <div className="space-y-6">
      {innovationsData.map((innovation, index) => (
        <div key={innovation.id} className="bg-gray-700 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-medium">Innovation {index + 1}</h3>
            <button
              onClick={() => removeInnovation(innovation.id)}
              className="text-red-400 hover:text-red-300"
            >
              Delete
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-white mb-2">Title</label>
              <input
                type="text"
                value={innovation.title}
                onChange={(e) => handleInnovationChange(index, 'title', e.target.value)}
                className="w-full bg-gray-600 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-white mb-2">Description</label>
              <textarea
                value={innovation.description}
                onChange={(e) => handleInnovationChange(index, 'description', e.target.value)}
                rows={3}
                className="w-full bg-gray-600 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-white mb-2">Technologies</label>
              <input
                type="text"
                value={innovation.technologies.join(', ')}
                onChange={(e) => handleInnovationChange(index, 'technologies', e.target.value.split(',').map(t => t.trim()))}
                className="w-full bg-gray-600 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Comma-separated technologies"
              />
            </div>

            <div>
              <label className="block text-white mb-2">Status</label>
              <select
                value={innovation.status}
                onChange={(e) => handleInnovationChange(index, 'status', e.target.value)}
                className="w-full bg-gray-600 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="Planning">Planning</option>
                <option value="In Development">In Development</option>
                <option value="Testing">Testing</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          </div>
        </div>
      ))}
      
      <button
        onClick={addInnovation}
        className="btn btn-primary flex items-center gap-2"
      >
        <FaPlus /> Add Innovation
      </button>
    </div>
  );

  // Add this useEffect to load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('chatbotSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          ...parsedSettings
        }));
      } catch (error) {
        console.error('Error loading chatbot settings:', error);
      }
    }
  }, []);

  const syncToBackend = async () => {
    setLoading(true);
    try {
      await updatePortfolioData('profile', profileData);
      await updatePortfolioData('about', aboutData);
      await updatePortfolioData('skills', groupedSkills);
      await updatePortfolioData('experience', experienceData);
      await updatePortfolioData('education', educationData);
      await updatePortfolioData('blog', blogData);
      await updatePortfolioData('youtube', youtubeData);
      await updatePortfolioData('projects', projectsData);
      await updatePortfolioData('innovations', innovationsData);
      for (const testimonial of testimonialsData) {
        await updatePortfolioData('testimonials', testimonial);
      }
      updateSocialLinks(socialLinksData);
      alert('Frontend data synced to backend!');
    } catch (err) {
      alert('Failed to sync: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Helmet>
        <title>Admin Dashboard | Dhiraj T H</title>
      </Helmet>
      {/* Sync to Backend Button */}
      <div className="flex justify-end p-4">
        <button
          onClick={syncToBackend}
          className="btn btn-primary"
          disabled={loading}
        >
          Sync to Backend
        </button>
      </div>
      
      {/* Header */}
      <header className="bg-secondary/90 backdrop-blur-sm shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
          <button
            onClick={handleBackToHome}
              className="flex items-center gap-2 text-white hover:text-primary transition-colors"
          >
              <FaArrowLeft />
              Back to Home
          </button>
            
            <div className="flex items-center gap-4">
              {hasChanges && (
                <button
                  onClick={saveChanges}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <FaSave />
                  )}
                  Save Changes
                </button>
              )}
        </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-4">
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'profile' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaUser />
                  Profile
                </button>
                <button
                  onClick={() => setActiveTab('about')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'about' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaUserAlt />
                  About
                </button>
                <button
                  onClick={() => setActiveTab('skills')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'skills' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaCode />
                  Skills
                </button>
                <button
                  onClick={() => setActiveTab('experience')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'experience' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaBriefcase />
                  Experience
                </button>
                <button
                  onClick={() => setActiveTab('education')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'education' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaGraduationCap />
                  Education
                </button>
                <button
                  onClick={() => setActiveTab('blog')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'blog' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaBlog />
                  Blog
                </button>
                <button
                  onClick={() => setActiveTab('youtube')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'youtube' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaYoutube />
                  YouTube
                </button>
                <button
                  onClick={() => setActiveTab('projects')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'projects' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaProjectDiagram />
                  Projects
                </button>
                <button
                  onClick={() => setActiveTab('testimonials')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'testimonials' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaQuoteRight />
                  Testimonials
                </button>
                <button
                  onClick={() => setActiveTab('footer')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'footer' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaCog />
                  Footer
                </button>
                <button
                  onClick={() => setActiveTab('chatbot')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'chatbot' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaCog />
                  Chatbot
                </button>
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'messages' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaShareAlt />
                  Messages
                </button>
                <button
                  onClick={() => setActiveTab('email')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'email' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaCog />
                  Email Settings
                </button>
                <button
                  onClick={() => setActiveTab('social')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'social' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaPlus />
                  Social Links
                </button>
                <button
                  onClick={() => setActiveTab('innovations')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'innovations' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaLightbulb />
                  Innovations
                </button>
                <button
                  onClick={() => setActiveTab('patents')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'patents' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaPlus />
                  Patents
                </button>
                <button
                  onClick={() => setActiveTab('sections')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'sections' ? 'bg-primary text-white' : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <FaSitemap />
                  Sections
                </button>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <EditHero
                profileData={profileData}
                setProfileData={setProfileData}
                handleProfileChange={handleProfileChange}
                saving={isSaving}
                setHasChanges={setHasChanges}
              />
            )}
            {activeTab === 'about' && <AboutEditor />}
            {activeTab === 'skills' && <SkillsEditor />}
            {activeTab === 'experience' && <ExperienceEditor />}
            {activeTab === 'education' && <EducationEditor />}
            {activeTab === 'blog' && <BlogEditor />}
            {activeTab === 'youtube' && <YouTubeEditor />}
            {activeTab === 'projects' && <ProjectsEditor />}
            {activeTab === 'testimonials' && <TestimonialsEditor />}
            {activeTab === 'footer' && <FooterSettings />}
            {activeTab === 'chatbot' && <ChatbotSettings />}
            {activeTab === 'messages' && <MessagesViewer />}
            {activeTab === 'email' && <EmailSettings />}
            {activeTab === 'social' && <SocialLinksEditor />}
            {activeTab === 'innovations' && <InnovationsEditor />}
            {activeTab === 'patents' && <PatentsEditor />}
            {activeTab === 'sections' && <SectionEditor />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;